// App.js or App.tsx
import React from "react";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import Home from './components/pages/Home/Home';
import Contact from "./components/pages/Contact/Contact";
import Elders from "./components/pages/Elders/Elders";
import Error from "./components/pages/Error/Error";
import About from "./components/pages/About/About";
import Navbar from "./components/base/Navbar/Navbar";
import History from "./components/pages/History/History";
import './App.css';
import Application from "./components/pages/Application/Application";
import CorporateInformation from "./components/pages/CorporateInformation/CorporateInformation";
import DataProtectionDeclaration from "./components/pages/DataProtectionDeclaration/DataProtectionDeclaration";
import ScrollToTop from "./components/functionallity/scrollToTop";
import KidsPlayground from "./components/pages/KidsPlayground/KidsPlayground";
import OnlineShop from "./components/pages/Shop/OnlineShop";
import ProductDetail from "./components/pages/Shop/ProductDetail";
import ShopCartReview from "./components/pages/Shop/ShopCartReview";
import Account from "./components/pages/Account/Account";
import PasswordReset from "./components/pages/Auth/PasswordReset";
import AdminUserManagement from "./components/pages/AdminUserManagement/AdminUserManagement";
import ShopReservationsManagement from "./components/pages/ShopReservationsManagement/ShopReservationsManagement";
import MyShopReservations from "./components/pages/MyShopReservations/MyShopReservations";
import { loadAuthSession } from "./services/authStorage";
import { ConsentProvider } from "./consent/ConsentContext";
import CookieConsentManager from "./components/consent/CookieConsentManager/CookieConsentManager";

const AdminRoute = ({ children }: { children: React.ReactElement }) => {
    const session = loadAuthSession();

    if (!session) {
        return <Navigate to="/konto" replace />;
    }

    if (session.user.role !== "administrator") {
        return <Navigate to="/" replace />;
    }

    return children;
};

const ShopManagerRoute = ({ children }: { children: React.ReactElement }) => {
    const session = loadAuthSession();

    if (!session) {
        return <Navigate to="/konto" replace />;
    }

    if (session.user.role !== "shopmanager") {
        return <Navigate to="/" replace />;
    }

    return children;
};

const AuthenticatedRoute = ({ children }: { children: React.ReactElement }) => {
    const session = loadAuthSession();

    if (!session) {
        return <Navigate to="/konto" replace />;
    }

    return children;
};

const AppLayout = () => {
    const location = useLocation();
    const hideNavbar =
        location.pathname.startsWith("/shop")
        && !location.pathname.startsWith("/shopverwaltung");

    return (
        <>
            <ScrollToTop/>
            {!hideNavbar && <Navbar />}
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/ueber-uns" element={<About />} />
                <Route path="/die-geschichte" element={<History />} />
                <Route path="/die-aeltesten" element={<Elders />} />
                <Route path="/kontakt" element={<Contact />} />
                <Route path="/bewerbung" element={<Application />} />
                <Route path="/impressum" element={<CorporateInformation />} />
                <Route path="/datenschutz" element={<DataProtectionDeclaration/>} />
                <Route path="/hexenwerkstatt" element={<KidsPlayground />} />
                <Route path="/shop" element={<OnlineShop />} />
                <Route path="/shop/:shopSection" element={<OnlineShop />} />
                <Route path="/shop/produkt/:productId" element={<ProductDetail />} />
                <Route path="/shop/warenkorb" element={<ShopCartReview />} />
                <Route path="/konto" element={<Account />} />
                <Route path="/konto/reservierungen" element={<AuthenticatedRoute><MyShopReservations /></AuthenticatedRoute>} />
                <Route path="/admin/mitglieder" element={<AdminRoute><AdminUserManagement /></AdminRoute>} />
                <Route
                    path="/shopverwaltung/reservierungen"
                    element={<ShopManagerRoute><ShopReservationsManagement /></ShopManagerRoute>}
                />
                <Route path="/passwort-zuruecksetzen" element={<PasswordReset />} />
                <Route path="*" element={<Error />} />
            </Routes>
            <CookieConsentManager />
        </>
    );
};

export default function App() {
    return (
        <ConsentProvider>
            <BrowserRouter>
                <AppLayout />
            </BrowserRouter>
        </ConsentProvider>
    );
}
